import { CheckRounded, FileCopyRounded, RemoveRedEyeOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import TruncatedText from 'js/components/elements/TruncatedText';
import { CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT } from 'js/constants/connectionCategories';
import { FILE_LIST_MODE_TYPES } from 'js/constants/paginationConstants';
import * as V1_ROUTES from 'js/constants/pathnames';
import DateUtils from 'js/lib/DateUtils';
import FormatUtils from 'js/lib/FormatUtils';
import toInteger from 'lodash/toInteger';
import * as V2_ROUTES from 'new/constants/pathnames';
import { Button } from 'new/elements/Button';
import { Checkbox } from 'new/elements/Checkbox/Checkbox';
import { ChipBadge } from 'new/elements/ChipBadge';
import { FileItemsListActions } from 'new/elements/FileItemsList/FileItemsListActions';
import { FileItemListRow, IngestionStatusInfo } from 'new/elements/FileItemsList/types';
import { ModalFilePreview } from 'new/elements/ModalFilePreview';
import { Pagination } from 'new/elements/Pagination';
import { Stack } from 'new/elements/Stack';
import { Table } from 'new/elements/Table';
import { Column, TableProps } from 'new/elements/Table/types';
import Typography from 'new/elements/Typography';
import { ResourceStatsFile } from 'new/hooks/adapters/useResourceStatsFiles';
import { useModals } from 'new/hooks/useModals';
import { FileUtil } from 'new/utils/FileUtil';
import React, { useCallback, useMemo } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Link } from 'react-router-dom';
import { shouldHideRecordsForResource } from 'js/lib/PipelineUtils';
import styles from './FileItemList.module.scss';

interface FileItemsListProps {
  loadMore?: (page: number) => void;
  onFilePreviewClicked?: (row: any) => void;
  resourceType: string;
  resourceId: string;
  mode?: keyof typeof FILE_LIST_MODE_TYPES;
  items: any[];
  connectionCategory: string;
  writeStatus?: string;
  pageCount: number;
  currentPage: number;
  data?: any;
  // Files
  isMultipleReingestActive?: boolean;
  isAnyFileEligibleForReingestion?: boolean;
  isAdaptiveFlow?: boolean;
  selectedFiles: Map<string, any>;
  addFile(a: string, b: any): void;
  removeFile(a: string): void;
  clearFiles(): void;
}

interface File {
  metadata?: any;
  name: any;
  dataSourceId?: any;
  data_credentials_id?: any;
  resourceId?: any;
  size?: any;
  bucket?: any;
}

export const FileItemsList = (
  props: FileItemsListProps & Omit<TableProps<any>, 'onEndReached' | 'rows' | 'columns'>,
) => {
  const {
    resourceType,
    mode,
    items = [],
    loading,
    loadMore,
    accessoryData,
    onFilePreviewClicked,
    selectedFiles,
    addFile,
    removeFile,
    clearFiles,
    isMultipleReingestActive = false,
    isAnyFileEligibleForReingestion = false,
    data,
    ...rest
  } = props;

  // check if we hide records for this flow type
  const hideRecords = shouldHideRecordsForResource(data);

  const { openModal } = useModals();

  const onClickPreview = (file: File) => () => {
    const { dataSourceId, data_credentials_id, resourceId, size, name, bucket } = file;

    /**
     * For files, we must display the display_path as name
     */
    const displayName = file?.metadata?.display_path ?? file.name;

    if (onFilePreviewClicked) {
      onFilePreviewClicked({ ...props, name, bucket, data_credentials_id });
    } else {
      openModal(ModalFilePreview.modalConfig.name, {
        fileName: name,
        displayFileName: displayName,
        fileId: dataSourceId,
        credentialId:
          mode === FILE_LIST_MODE_TYPES.MODE_QUARANTINE_FILES ? data_credentials_id : undefined,
        noConnection: mode === FILE_LIST_MODE_TYPES.MODE_QUARANTINE_FILES,
        filePath: mode === 'MODE_QUARANTINE_FILES' ? bucket + name : undefined,
        size,
        resourceType: mode !== FILE_LIST_MODE_TYPES.MODE_QUARANTINE_FILES ? resourceType : null,
        resourceId: mode !== FILE_LIST_MODE_TYPES.MODE_QUARANTINE_FILES ? resourceId : null,
      });
    }
  };

  const onChangePage = (_e: any, page: number) => {
    loadMore(page);
    clearFiles?.(); // clear the selected files when the page changes
  };

  const onSelectFile = useCallback((checked: boolean, file: any) => {
    if (checked) {
      addFile(file.name, file);
    } else {
      removeFile(file.name);
    }
  }, []);

  const isSelectedCheck = (value: any) => {
    if (!selectedFiles) return false;
    if (typeof value === 'string') return selectedFiles.has(value);
    return selectedFiles.has(value?.name || value?.metadata?.display_path);
  };

  const isAllSelected = (() => {
    const paths = FileUtil.getFilesAllowedToReingest(items, rest?.connectionCategory, mode).map(
      (file) => file.name,
    ) as string[];
    return paths.length !== 0 && selectedFiles.size !== 0 && paths.length === selectedFiles.size;
  })();

  const onSelectAll = () => {
    if (!items.length) return;
    if (isAllSelected) {
      clearFiles();
    } else {
      const values = FileUtil.getFilesAllowedToReingest(items, rest?.connectionCategory, mode);

      values.forEach((file) => addFile(file.name, file));
    }
  };

  const generatedColumns = useMemo(
    () =>
      getColumns(
        resourceType,
        mode,
        props.connectionCategory,
        {
          isAllSelected,
          onSelectAll,
          isSelectedCheck,
          onSelectFile,
          isMultipleReingestActive,
          isAnyFileEligibleForReingestion,
        },
        hideRecords,
      ),
    [resourceType, isAllSelected, isSelectedCheck, mode, onSelectAll, hideRecords],
  );

  const getRowClassName = useCallback(
    (row: any) => {
      // computes the class name for a row in a data table, based on a selected condition.
      return isSelectedCheck(row) ? styles.fileReingestHighlightBg : styles.rowClassName;
    },
    [isSelectedCheck],
  );

  return (
    <Stack>
      <Table
        {...rest}
        key={items?.length}
        accessoryData={{ ...props, onClickPreview, ...accessoryData }}
        disableVirtualization
        headClassName={styles.headClassName}
        getRowClassName={getRowClassName}
        getRowTdClassName={() => styles.rowTdClassName}
        parentScrollRef={rest.height ? undefined : rest.parentScrollRef}
        isSkeletonLoadingMode
        columns={generatedColumns}
        rows={items}
        loading={loading}
      />

      {!props.pageCount || props.pageCount === 1 ? null : (
        <Stack className={styles.pagination}>
          <Pagination count={props.pageCount} page={props.currentPage} onChange={onChangePage} />
        </Stack>
      )}
    </Stack>
  );
};

const buttonSx = {
  color: 'white',
  textTransform: 'none',
  fontSize: 12,
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
};

const stopPropagation = (e: any) => e?.stopPropagation();

const TooltipActions = ({ onClickPreview, path }) => {
  const [copied, setCopied] = React.useState(false);
  const Wrapper = !copied ? CopyToClipboard : React.Fragment;

  return (
    <Stack direction="row" alignItems="center" onClick={stopPropagation}>
      <Stack flex={1}>
        <Button onClick={onClickPreview} sx={buttonSx} startIcon={<RemoveRedEyeOutlined />}>
          Preview
        </Button>
      </Stack>
      <Stack flex={1}>
        <Wrapper text={!copied ? path : undefined} onCopy={() => setCopied(true)}>
          <Button
            sx={buttonSx}
            disabled={copied}
            startIcon={!copied ? <FileCopyRounded /> : <CheckRounded />}
          >
            {!copied ? 'Copy path' : 'Copied'}
          </Button>
        </Wrapper>
      </Stack>
    </Stack>
  );
};

function getColumns(
  resourceType: string,
  mode: keyof typeof FILE_LIST_MODE_TYPES,
  connectionCategory: string,
  fileProps: any,
  hideRecords: boolean = false,
) {
  const isDataSinkType = resourceType === 'data_sinks';
  const isReadMode = mode === FILE_LIST_MODE_TYPES.MODE_READ;
  const isQuarantineMode = mode === FILE_LIST_MODE_TYPES.MODE_QUARANTINE_FILES;

  const {
    isAllSelected,
    isSelectedCheck,
    onSelectFile,
    onSelectAll,
    isMultipleReingestActive,
    isAnyFileEligibleForReingestion,
  } = fileProps;

  return [
    {
      name: 'Details',
      field: 'name',
      width: 30,
      renderRow(row: FileItemListRow) {
        if (!row) return null;
        const displayName = row.metadata?.display_path || row.name;

        const metadata = (() => {
          const isConnectionCategoryWithMetadata =
            connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT && row.metadata;

          if (isConnectionCategoryWithMetadata) {
            let metaDataDate = row.metadata.date
              ? DateUtils.convertDateStringToFormat(
                  toInteger(row.metadata.date),
                  'MMM D, YYYY [at] h:mma z',
                  true,
                  true,
                  true,
                )
              : '';
            return (
              <Typography variant="caption">
                Email from: <Typography variant="body2">{row.metadata.from_email}</Typography> -
                Received on: <Typography variant="body2">{metaDataDate}</Typography> - Subject:{' '}
                <Typography variant="body2">
                  <TruncatedText text={row.metadata.subject} lines={1} />
                </Typography>
              </Typography>
            );
          }
        })();

        let lastChange = row?.updated_at;

        if (isReadMode) {
          lastChange = row?.lastIngested;
        } else if (isDataSinkType && !isQuarantineMode) {
          lastChange = row?.lastWritten;
        }

        const lastChangeFormatted = DateUtils.getDateString(lastChange) ?? '';
        const sizeFormatted = FormatUtils.bytesToString(row.size) ?? '';

        return (
          <Stack flex={1}>
            {displayName ? (
              <Tooltip
                title={
                  <Stack
                    minWidth={200}
                    direction="column"
                    alignItems="stretch"
                    justifyContent="flex-start"
                  >
                    {displayName}
                    <TooltipActions path={row.name} onClickPreview={row.onClickPreview(row)} />
                  </Stack>
                }
              >
                <span>
                  <TruncatedText text={displayName} lines={1} />
                </span>
              </Tooltip>
            ) : null}
            {metadata}
            <Typography variant="body3">
              {[lastChangeFormatted, sizeFormatted].filter(Boolean).join(' - ')}
            </Typography>
          </Stack>
        );
      },
    },
    {
      name: 'Size',
      sortable: true,
      field: 'size',
      renderRow: ({ size }: FileItemListRow) => FormatUtils.bytesToString(size),
    },
    !(mode === FILE_LIST_MODE_TYPES.MODE_WRITE)
      ? {
          name: mode === FILE_LIST_MODE_TYPES.MODE_READ ? 'Nexset' : 'Credential',
          field: mode === FILE_LIST_MODE_TYPES.MODE_READ ? 'dataSetId' : 'data_credentials_id',
          renderRow(row: { dataSetId: number; data_credentials_id: number }) {
            if (mode === FILE_LIST_MODE_TYPES.MODE_READ) {
              const dataSetID = row.dataSetId;
              const to = `${V1_ROUTES.ROUTE_REDESIGN}${V2_ROUTES.ROUTE_MY_NEXSETS}`;

              return <Link to={`${to}/${dataSetID}`}>{dataSetID}</Link>;
            }

            const credentialsID = row.data_credentials_id;
            const to = `${V1_ROUTES.ROUTE_REDESIGN}${V2_ROUTES.ROUTE_CREDENTIALS}`;

            return <Link to={`${to}/${credentialsID}`}>{credentialsID}</Link>;
          },
        }
      : null,
    ...(hideRecords
      ? []
      : [
          {
            name: 'Records Count',
            field: 'recordCount',
            renderRow: ({ recordCount }: FileItemListRow) => FormatUtils.formatNumber(recordCount),
          },
        ]),
    !isQuarantineMode
      ? {
          name: 'Ingestion Status',
          sortable: true,
          field: 'ingestionStatus',
          renderRow(row: FileItemListRow) {
            const info =
              IngestionStatusInfo[
                mode === FILE_LIST_MODE_TYPES.MODE_READ ? row.ingestionStatus : row.writeStatus
              ] || IngestionStatusInfo.IN_PROGRESS;
            return (
              <ChipBadge
                label={info.title}
                color={info.color as any}
                className={styles.chipBadge}
              />
            );
          },
        }
      : null,
    {
      /**
       * This column is dynamically rendered for file lists.
       * It supports features like "Select All" option via checkbox and integrates with the `FileItemsListActions` component to offer file-specific operations.
       */
      name: (
        <div>
          {isAnyFileEligibleForReingestion && isMultipleReingestActive && (
            <Checkbox
              label="Select All"
              checked={isAllSelected}
              onChange={onSelectAll}
              sx={{ minWidth: 100, marginLeft: '28px' }}
            />
          )}
        </div>
      ),
      numeric: false,
      field: 'actions',
      renderRow(row: FileItemListRow) {
        const name = row?.metadata?.display_path || row.name;
        const isSelected = isSelectedCheck(name);

        return (
          // @ts-ignore
          <FileItemsListActions
            {...row}
            mode={mode}
            isSelected={isSelected}
            onSelectFile={onSelectFile}
            isMultipleReingestActive={isMultipleReingestActive}
          />
        );
      },
    },
  ].filter((c) => !!c) as Column<ResourceStatsFile & any>[];
}
